/**
 * The Profile tab's display half (profile-tab-reskin; web-account-pages D8): the
 * identity strip, My info rows, Payment methods, Preferences (the time zone in
 * words) and the footer, plus the header and the card-removal sheet. It takes
 * props and reads nothing: the Mini App's ProfilePage feeds it from its own reads,
 * the studio website's phone Profile from the website session's.
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/profile-tab.html (round 3b).
 */
import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { LEGAL_PAGE_PATHS } from '@telify/shared/browser';
import { FlowBackButton } from '@/components/booking/FlowBackButton';
import { ClientSheet, clientSheetStyles as sheetStyles } from '@/components/client/ClientSheet';
import { formatUsPhoneWithCountry } from '@/components/forms/formatPhone';
import type { ProfilePayload, ProfileUpdateInput, SavedCard } from '@/types/profile';
import { timeZoneInWords } from '@/utils/timeZoneWords';
import styles from './ProfilePage.module.css';
import { isLinkCard, LINK_LABEL } from '@telify/shared';

// ── Icons (mockup SVGs) ──────────────────────────────────────────────────────
const ChevronIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
);
const RemoveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);
const CardOrbIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
);

const MEMBER_SINCE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
export type EditableProfileField = keyof ProfileUpdateInput;

function formatProfilePhone(value: string | null): string | null {
  if (!value) return value;
  return formatUsPhoneWithCountry(value);
}

/** Initials from first/last name (avatar fallback when avatarUrl is null). */
function initials(firstName: string, lastName: string | null): string {
  const a = firstName.trim().charAt(0);
  const b = (lastName ?? '').trim().charAt(0);
  return (a + b).toUpperCase() || a.toUpperCase() || '·';
}

/** The device timezone in words, the same browser-detected zone used across client surfaces. */
function deviceTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone ? timeZoneInWords(zone) : 'Local time';
  } catch {
    return 'Local time';
  }
}

function titleBrand(brand: string): string {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

/** "Visa ···· 4242"; a Link payment method (made-up digits) reads Link (wallets plan). */
function cardName(card: { brand: string; last4: string | null }): string {
  if (isLinkCard(card.brand)) return LINK_LABEL;
  return card.last4 ? `${titleBrand(card.brand)} ···· ${card.last4}` : titleBrand(card.brand);
}

function CardBrandBadge({ brand }: { brand: string }) {
  const b = brand.toLowerCase();
  if (b === 'visa') return <div className={`${styles.cardBrand} ${styles.visa}`}>VISA</div>;
  if (b === 'mastercard') return <div className={`${styles.cardBrand} ${styles.mc}`} aria-label="Mastercard" />;
  return <div className={`${styles.cardBrand} ${styles.generic}`}>{brand.slice(0, 4).toUpperCase()}</div>;
}

export function ProfileHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className={styles.pageHeaderSection}>
      <FlowBackButton className={styles.back} onClick={onBack} />
      <div className={styles.pageTitleBlock}>
        <h1 className={styles.pageTitle}>Profile</h1>
      </div>
    </div>
  );
}

function MyInfoRow({ label, value, onEdit }: { label: string; value: string | null; onEdit: () => void }) {
  return (
    <button type="button" className={styles.setRow} onClick={onEdit} data-testid={`myinfo-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className={styles.setLabel}>{label}</span>
      <span className={styles.setValue}>{value || 'Not set'}</span>
      <ChevronIcon className={styles.setChev} />
    </button>
  );
}

export interface ProfileViewProps {
  profile: ProfilePayload;
  cards: { status: 'loading' | 'unavailable' | 'ready'; list: SavedCard[] };
  onEdit: (field: EditableProfileField) => void;
  onRemoveCard: (card: SavedCard) => void;
  /** A legal link's click (the app opens it outside the Mini App); left out, the link opens in a new tab (row P3). */
  onLegalLink?: (event: MouseEvent, path: string) => void;
  /** Its own card under Preferences (the studio website's Sign out). */
  afterPreferences?: ReactNode;
  /** The page's sheets. */
  children?: ReactNode;
}

export function ProfileView({ profile, cards, onEdit, onRemoveCard, onLegalLink, afterPreferences, children }: ProfileViewProps) {
  const { identity: id, myInfo } = profile;
  const fullName = [id.firstName, id.lastName].filter(Boolean).join(' ') || 'You';
  const memberSince = MEMBER_SINCE_FMT.format(new Date(id.memberSince));
  // A legal page never replaces the account page: the app opens it outside the Mini App, a
  // browser in a new tab.
  const legalLink = (path: string) =>
    onLegalLink ? { onClick: (event: MouseEvent) => onLegalLink(event, path) } : { target: '_blank', rel: 'noopener noreferrer' };

  return (
    <div data-testid="profile-page">
      {/* Identity strip — first element of the scrolling content (scrolls away). */}
      <div className={styles.idStrip}>
        {id.avatarUrl ? (
          <div
            className={styles.idAvatar}
            style={{ backgroundImage: `url("${id.avatarUrl.replace(/[\\"]/g, (c) => `\\${c}`)}")` }}
            aria-hidden="true"
          />
        ) : (
          <div className={`${styles.idAvatar} ${styles.avatarFallback}`} aria-hidden="true" data-testid="avatar-fallback">
            {initials(id.firstName, id.lastName)}
          </div>
        )}
        <div className={styles.idText}>
          <div className={styles.idName}>{fullName}</div>
          <div className={styles.idMeta}>
            {[id.username ? `@${id.username}` : null, `Member since ${memberSince}`].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className={styles.idSessions}>
          <div className={styles.idSessionsN}>{id.sessionsCompleted}</div>
          <div className={styles.idSessionsL}>Sessions</div>
        </div>
      </div>

      <div className={styles.body}>
        {/* MY INFO */}
        <div className={styles.groupLabel}><span className={styles.secKicker}>My info</span></div>
        <section className={styles.gcard}>
          <MyInfoRow label="First name" value={myInfo.firstName} onEdit={() => onEdit('firstName')} />
          <MyInfoRow label="Last name" value={myInfo.lastName} onEdit={() => onEdit('lastName')} />
          <MyInfoRow label="Email" value={myInfo.email} onEdit={() => onEdit('email')} />
          <MyInfoRow label="Phone" value={formatProfilePhone(myInfo.phone)} onEdit={() => onEdit('phone')} />
        </section>

        {/* PAYMENT METHODS */}
        <div className={styles.groupLabel}><span className={styles.secKicker}>Payment methods</span></div>
        <section className={styles.gcard}>
          {cards.status === 'loading' ? (
            <div className={styles.payQuiet} data-testid="cards-loading">Loading saved cards…</div>
          ) : cards.status === 'unavailable' ? (
            <div className={styles.payQuiet} data-testid="cards-unavailable">Saved cards are unavailable right now.</div>
          ) : cards.list.length > 0 ? (
            <>
              {cards.list.map((card) => (
                <div key={card.id} className={styles.payRow} data-testid="saved-card">
                  <CardBrandBadge brand={card.brand} />
                  <div className={styles.payMain}>
                    <div className={styles.payNum}>{cardName(card)}</div>
                    {card.expMonth != null && card.expYear != null && (
                      <div className={styles.payExp}>{`Expires ${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.payRemove}
                    aria-label={card.last4 ? `Remove card ending ${card.last4}` : `Remove ${cardName(card)}`}
                    onClick={() => onRemoveCard(card)}
                    data-testid="remove-card"
                  >
                    <RemoveIcon />
                  </button>
                </div>
              ))}
              <div className={styles.payNote}>Saved when you choose to at checkout.</div>
            </>
          ) : (
            <div className={styles.payEmpty} data-testid="cards-empty">
              <div className={styles.payOrb}><CardOrbIcon /></div>
              <div className={styles.payEmptyT}>No cards saved yet</div>
              <div className={styles.payEmptyS}>To save a card, tick Save at checkout when you pay.</div>
            </div>
          )}
        </section>

        {/* PREFERENCES — timezone (display-only) */}
        <div className={styles.groupLabel}><span className={styles.secKicker}>Preferences</span></div>
        <section className={styles.gcard}>
          <div className={styles.setRow}>
            <span className={styles.setLabel}>Time zone</span>
            <span className={styles.setValue}>{deviceTimezone()} · Set by this device</span>
          </div>
        </section>
        {afterPreferences}

        {/* FOOTER — the one place Telify brands a tenant app; the legal pages open outside
            the Mini App (registration-privacy-consent, mockup registration-consent.html frame 4). */}
        <div className={styles.foot}>
          <div className={styles.footLinks}>
            <a href={LEGAL_PAGE_PATHS.privacyPolicy} {...legalLink(LEGAL_PAGE_PATHS.privacyPolicy)}>Privacy</a>
            <span aria-hidden="true">·</span>
            <a href={LEGAL_PAGE_PATHS.termsOfService} {...legalLink(LEGAL_PAGE_PATHS.termsOfService)}>Terms</a>
          </div>
          <div className={styles.footBrand}>Powered by <b>Telify</b></div>
          <div className={styles.footVer}>v{__APP_VERSION__}</div>
        </div>
      </div>

      {children}
    </div>
  );
}

/** One action row in its own card under Preferences (the studio website's Sign out, row P2). */
export function ProfileActionCard({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <section className={styles.gcard}>
      <button type="button" className={styles.setRow} onClick={onPress}>
        <span className={styles.setLabel}>{label}</span>
      </button>
    </section>
  );
}

/**
 * Glass confirm sheet for card removal — the shared ClientSheet idiom. `open`-
 * controlled; latches the last card so its copy survives the exit animation after
 * `card` clears. Removal failure is caught (no unhandled rejection), keeps the
 * sheet open, and shows an inline error.
 */
export function RemoveCardSheet({
  open,
  card,
  removing,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  card: SavedCard | null;
  removing: boolean;
  error: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [shown, setShown] = useState<SavedCard | null>(card);
  useEffect(() => {
    if (card) setShown(card);
  }, [card]);

  const handleRemove = async () => {
    try {
      await onConfirm();
    } catch {
      /* stay open — the inline error UI rides `error` */
    }
  };

  return (
    <ClientSheet
      open={open}
      onClose={onClose}
      kicker="Remove card"
      title="Remove this card?"
      dismissable={!removing}
      testId="remove-card-sheet"
    >
      <p className={sheetStyles.copy}>
        Your{' '}
        <b>
          {shown ? cardName(shown) : 'card'}
        </b>{' '}
        will be removed. To save it again, tick Save at checkout.
      </p>
      {error && (
        <div className={sheetStyles.error} data-testid="remove-card-error" role="alert">
          Couldn’t remove your card. Please try again.
        </div>
      )}
      <div className={sheetStyles.actions}>
        <button type="button" className={sheetStyles.keep} onClick={onClose} disabled={removing}>
          Keep card
        </button>
        <button
          type="button"
          className={sheetStyles.danger}
          onClick={handleRemove}
          disabled={removing}
          data-testid="confirm-remove-card"
        >
          {removing ? 'Removing…' : 'Remove card'}
        </button>
      </div>
    </ClientSheet>
  );
}
